import i18nFactory from '@yandex-int/i18n';
import { get } from 'lodash';

import { mapStringToCurrencySymbol } from '../../../mappers/common/common';
import { ValidationResultItem } from '../../../typings/common';
import { CurrencyCode } from '../../../typings/graphql-grid';
import { createErrorsMapper } from '../../ErrorsMapper/ErrorsMapper';
import { getFormattedPrice } from '../../FormatHelpers/FormatHelpers';

import * as keyset from './GroupFiltersErrorsTextMapper.i18n';

const i18n = i18nFactory(keyset);

const getPriceFromValidationParams = (validationResult: ValidationResultItem): string => {
    return getFormattedPrice(get(validationResult, 'params.moneyPriceValue', 0).toString());
};

const getCurrencyFromValidationParams = (validationResult: ValidationResultItem): string => {
    return mapStringToCurrencySymbol(get(validationResult, 'params.moneyValue.currencyCode', CurrencyCode.RUB));
};

export const filtersTextErrorMapper = createErrorsMapper<{
    isTreeTab: boolean;
}>(
    ({ idx }) => [
        {
            path: ['filterItself'],
            codes: [
                {
                    code: 'CollectionDefectIds.Gen.MUST_NOT_CONTAIN_DUPLICATED_OBJECTS',
                    message: (): string => {
                        return i18n('filters-must-be-unique');
                    }
                }
            ]
        },
        {
            path: ['allConditions'],
            codes: [
                {
                    code: 'CollectionDefectIds.Gen.CANNOT_BE_EMPTY',
                    message: (): string => {
                        return i18n('conditions-cannot-be-empty');
                    }
                },
                {
                    code: 'PerformanceFilterDefects.PerformanceFilterDefectIds.MUST_CONTAIN_ANY_MORE_CONDITIONS',
                    message: (): string => {
                        return i18n('conditions-cannot-be-empty');
                    }
                },
                {
                    code: 'CollectionDefectIds.Gen.MUST_NOT_CONTAIN_DUPLICATED_ELEMENTS',
                    message: (): string => {
                        return i18n('conditions-must-be-unique');
                    }
                }
            ]
        },
        {
            path: ['conditions', idx],
            codes: [
                {
                    code: 'CollectionDefectIds.Gen.CANNOT_BE_EMPTY',
                    message: (err, params): string => {
                        if (get(params, 'isTreeTab')) {
                            return i18n('categories-cannot-be-empty');
                        }

                        return i18n('conditions-cannot-be-empty');
                    }
                },
                {
                    code: 'DefectIds.INVALID_FORMAT',
                    message: (): string => {
                        return i18n('condition-invalid-format');
                    }
                },
                {
                    code: 'CollectionDefectIds.Size.SIZE_MUST_BE_IN_INTERVAL',
                    message: (err: ValidationResultItem): string => {
                        return i18n('condition-control-interval-size', {
                            min: get(err, 'params.minSize', ''),
                            max: get(err, 'params.maxSize', '')
                        });
                    }
                },
                {
                    code: 'NumberDefectIds.MUST_BE_LESS_THEN_OR_EQUAL_TO_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('condition-control-less-or-equal-max', {
                            max: get(err, 'params.max', Number.MAX_SAFE_INTEGER)
                        });
                    }
                },
                {
                    code: 'NumberDefectIds.MUST_BE_GREATER_THAN_OR_EQUAL_TO_MIN',
                    message: (err: ValidationResultItem): string => {
                        return i18n('condition-control-greater-or-equal-min', {
                            min: get(err, 'params.min', 0)
                        });
                    }
                },
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => {
                        return i18n('condition-control-cannot-be-empty');
                    }
                },
                {
                    code: 'DefectIds.INVALID_VALUE',
                    message: (): string => {
                        return i18n('condition-control-invalid-value');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.RESTRICTED_CHARS_IN_FIELD',
                    message: (): string => {
                        return i18n('condition-prohibited-symbols');
                    }
                }
            ]
        },
        {
            path: ['priceCpc'],
            codes: [
                {
                    code: 'BidsDefects.CurrencyAmountDefects.SEARCH_PRICE_IS_NOT_GREATER_THAN_MIN',
                    message: (err: ValidationResultItem): string => {
                        return i18n('price-less-than-min', {
                            min: getPriceFromValidationParams(err),
                            currency: getCurrencyFromValidationParams(err)
                        });
                    }
                },
                {
                    code: 'BidsDefects.CurrencyAmountDefects.SEARCH_PRICE_IS_NOT_SMALLER_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('price-more-than-max', {
                            max: getPriceFromValidationParams(err),
                            currency: getCurrencyFromValidationParams(err)
                        });
                    }
                }
            ]
        },
        {
            path: ['priceCpa'],
            codes: [
                {
                    code: 'BidsDefects.CurrencyAmountDefects.SEARCH_PRICE_IS_NOT_GREATER_THAN_MIN',
                    message: (err: ValidationResultItem): string => {
                        return i18n('price-less-than-min', {
                            min: getPriceFromValidationParams(err),
                            currency: getCurrencyFromValidationParams(err)
                        });
                    }
                },
                {
                    code: 'BidsDefects.CurrencyAmountDefects.SEARCH_PRICE_IS_NOT_SMALLER_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('price-more-than-max', {
                            max: getPriceFromValidationParams(err),
                            currency: getCurrencyFromValidationParams(err)
                        });
                    }
                }
            ]
        },
        {
            path: ['name'],
            codes: [
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_LESS_THAN_MIN',
                    message: (err: ValidationResultItem): string => {
                        return i18n('name-less-than-min', {
                            min: get(err, 'params.minLength', 0)
                        });
                    }
                },
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err: ValidationResultItem): string => {
                        return i18n('name-greater-than-max', {
                            max: get(err, 'params.maxLength', 0)
                        });
                    }
                },
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => {
                        return i18n('name-cannot-be-empty');
                    }
                },
                {
                    code: 'BannerDefectIds.Gen.RESTRICTED_CHARS_IN_FIELD',
                    message: (err: ValidationResultItem): string => {
                        return i18n('name-prohibited-symbols');
                    }
                }
            ]
        }
    ],
    error => i18n('default-error', { errorCode: error.code })
);
